<template>
  <div class="background-info">
    <div class="background-info__wrapper">
      <h1 class="background-info__header">{{ $t('backgroundInfo.header') }}</h1>
      <div v-if="hasCompletedBackgroundInfo">
        <p class="background-info__completed-message">
          {{ $t('backgroundInfo.completedMessage') }}
        </p>
      </div>
      <form class="background-info__form" @submit="submitForm" v-else>
        <p>
          {{ $t('backgroundInfo.description') }}
        </p>

        <ol>
          <li class="uc-form-col">
            <p>
              {{ $t('backgroundInfo.formCol-1.questionRequired') }}
              <span class="background-info__question-required">*</span>
            </p>
            <p class="background-info__question-description">
              {{ $t('backgroundInfo.formCol-1.questionDescription') }}
            </p>
            <p v-if="showInputErrors && occupation.length === 0" class="error">
              {{ $t('backgroundInfo.formCol-1.errorMessage') }}
            </p>
            <div
              class="uc-form-checkbox"
              v-for="option in options.occupations"
              :key="option"
            >
              <input
                type="checkbox"
                :value="option"
                v-model="occupation"
                :id="option"
              />
              <label class="uc-form-label" :for="option">
                {{ option }}
              </label>
            </div>
            <template v-if="isCollegeEducated">
              <label class="uc-form-label occupations-label" for="college">{{
                $t('backgroundInfo.formCol-1.collegeLabel')
              }}</label>
              <input
                type="text"
                v-model="college"
                :placeholder="$t('backgroundInfo.formCol-1.collegePlaceHolder')"
                class="uc-form-input occupations-input"
                id="college"
              />
            </template>

            <template v-if="isWorkingFullTime">
              <label class="uc-form-label occupations-label" for="company">{{
                $t('backgroundInfo.formCol-1.companyLabel')
              }}</label>
              <input
                type="text"
                v-model="company"
                :placeholder="$t('backgroundInfo.formCol-1.companyPlaceHolder')"
                class="uc-form-input occupations-input"
                id="company"
              />
            </template>
          </li>

          <li class="uc-form-col">
            <p>
              {{ $t('backgroundInfo.formCol-2.description') }}
            </p>
            <p class="background-info__question-description">
              {{ $t('backgroundInfo.formCol-2.description-2') }}
            </p>

            <input
              type="text"
              :pattern="linkedInUrlPattern.source"
              v-model="linkedInUrl"
              placeholder="https://www.linkedin.com/in/yourname"
              class="linkedin-input uc-form-input"
              id="linkedin"
            />
            <p v-if="!isValidLinkedInUrl" class="error">
              {{ $t('backgroundInfo.formCol-2.errorMessage') }}
            </p>
          </li>

          <li class="uc-form-col">
            <p>
              {{ $t('backgroundInfo.formCol-3.description')
              }}<span class="background-info__question-required">*</span>
            </p>

            <label class="uc-form-label location-label">{{
              $t('backgroundInfo.formCol-2.countryLabel')
            }}</label>
            <v-select
              class="location-input"
              v-model="country"
              :options="countries"
              :searchable="true"
            />
            <template v-if="country === 'United States of America'">
              <label class="uc-form-label location-label">{{
                $t('backgroundInfo.formCol-3.stateLabel')
              }}</label>
              <v-select
                class="location-input"
                id="state"
                v-model="state"
                :options="states"
                :searchable="true"
              />
            </template>
            <template v-if="country">
              <label class="uc-form-label location-label" for="city">{{
                $t('backgroundInfo.formCol-3.cityLabel')
              }}</label>
              <input
                type="text"
                v-model="city"
                :placeholder="$t('backgroundInfo.formCol-3.cityPlaceHolder')"
                class="uc-form-input location-input"
                id="city"
              />
            </template>
            <p v-if="showInputErrors && !experience" class="error">
              {{ $t('backgroundInfo.formCol-3.errorMessage') }}
            </p>
          </li>

          <li class="uc-form-col">
            <p>
              {{ $t('backgroundInfo.formCol-4.description') }}
              <span class="background-info__question-required">*</span>
            </p>
            <p v-if="showInputErrors && !experience" class="error">
              {{ $t('backgroundInfo.formCol-4.errorMessage') }}
            </p>

            <table class="questions-table">
              <tr class="question-row">
                <div class="position-wrapper">
                  <div class="question-scroll-container">
                    <tr class="radio-question-row">
                      <td class="mobile-remove"></td>
                      <td
                        class="radio-question-selection-title"
                        v-for="title in experienceRadioQuestion.columnTitle"
                        v-bind:key="title"
                      >
                        {{ title }}
                      </td>
                    </tr>
                    <tr
                      class="radio-question-row"
                      v-for="(subquestion,
                      subquestionIndex) in experienceRadioQuestion.options"
                      :key="subquestion"
                    >
                      <td class="radio-question-cell">{{ subquestion }}</td>
                      <td
                        class="radio-question-selection-cell"
                        v-for="index in experienceRadioQuestion.columnTitle
                          .length"
                        :key="index"
                      >
                        <input
                          class="uc-form-input"
                          v-model="
                            experience[
                              experienceRadioQuestion.optionsAlias[
                                subquestionIndex
                              ]
                            ]
                          "
                          type="radio"
                          :value="index"
                        />
                      </td>
                    </tr>
                    <div class="mobile-pinned-questions-container">
                      <tr class="radio-question-row">
                        <td class="mobile-remove mobile-remove--shadow"></td>
                        <td
                          class="radio-question-selection-title radio-question-selection-title--hidden"
                          v-for="title in experienceRadioQuestion.columnTitle"
                          v-bind:key="title"
                        >
                          {{ title }}
                        </td>
                      </tr>
                      <tr
                        class="radio-question-row"
                        v-for="subquestion in experienceRadioQuestion.options"
                        v-bind:key="subquestion"
                      >
                        <td
                          class="radio-question-cell radio-question-cell--shadow"
                        >
                          {{ subquestion }}
                        </td>
                        <td
                          class="radio-question-selection-cell--hidden"
                          v-for="index in experienceRadioQuestion.columnTitle
                            .length"
                          v-bind:key="index"
                        >
                          <input class="uc-form-input" type="radio" />
                        </td>
                      </tr>
                    </div>
                  </div>
                </div>
              </tr>
            </table>
          </li>

          <li class="uc-form-col">
            <p>
              {{ $t('backgroundInfo.formCol-5.description') }}
            </p>
            <p class="background-info__question-description">
              {{ $t('backgroundInfo.formCol-5.description-2') }}
            </p>

            <div
              class="uc-form-checkbox"
              v-for="option in options.languages"
              :key="option"
            >
              <input
                type="checkbox"
                :value="option"
                v-model="languages"
                :id="option"
              />
              <label class="uc-form-label" :for="option">
                {{ option }}
              </label>
            </div>
            <div class="uc-form-checkbox">
              <input
                type="checkbox"
                value="other"
                id="other"
                @change="toggleAddLanguages"
              />
              <label for="other">{{
                $t('backgroundInfo.formCol-5.otherLabel')
              }}</label>
            </div>
            <input
              type="text"
              v-model="addedLanguages"
              class=" uc-form-input"
              v-if="showAddLanguages"
              :placeholder="$t('backgroundInfo.formCol-5.otherPlaceHolder')"
            />
          </li>
        </ol>
        <p class="error form-error" v-if="formError">{{ formError }}</p>
        <button
          class="uc-form-button submit-btn"
          type="submit"
          :disabled="invalidForm()"
        >
          {{ $t('general.submit') }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import NetworkService from '@/services/NetworkService'
import AnalyticsService from '@/services/AnalyticsService'
import { COUNTRIES, STATES, EVENTS } from '@/consts'

export default {
  name: 'background-info-view',
  data() {
    return {
      options: {
        occupations: [
          this.$root.$t('backgroundInfo.occupations.item-1'),
          this.$root.$t('backgroundInfo.occupations.item-2'),
          this.$root.$t('backgroundInfo.occupations.item-3'),
          this.$root.$t('backgroundInfo.occupations.item-4'),
          this.$root.$t('backgroundInfo.occupations.item-5'),
          this.$root.$t('backgroundInfo.occupations.item-6'),
          this.$root.$t('backgroundInfo.occupations.item-7'),
          this.$root.$t('backgroundInfo.occupations.item-8'),
        ],
        languages: [
          this.$root.$t('backgroundInfo.languages.item-1'),
          this.$root.$t('backgroundInfo.languages.item-2'),
          this.$root.$t('backgroundInfo.languages.item-3'),
          this.$root.$t('backgroundInfo.languages.item-4'),
          this.$root.$t('backgroundInfo.languages.item-5'),
          this.$root.$t('backgroundInfo.languages.item-6'),
          this.$root.$t('backgroundInfo.languages.item-7'),
          this.$root.$t('backgroundInfo.languages.item-8'),
          this.$root.$t('backgroundInfo.languages.item-9'),
        ],
      },
      showInputErrors: false,
      formError: '',
      occupation: [],
      linkedInUrl: '',
      experience: {
        tutoring: '',
        collegeCounseling: '',
        mentoring: '',
      },
      languages: [],
      showAddLanguages: false,
      addedLanguages: '',
      wasSubmitted: false,
      country: '',
      state: '',
      city: '',
      college: '',
      company: '',
      experienceRadioQuestion: {
        columnTitle: [
          this.$root.$t('backgroundInfo.columnTitle.item-1'),
          this.$root.$t('backgroundInfo.columnTitle.item-2'),
          this.$root.$t('backgroundInfo.columnTitle.item-3'),
          this.$root.$t('backgroundInfo.columnTitle.item-4'),
          this.$root.$t('backgroundInfo.columnTitle.item-5'),
        ],
        options: [
          this.$root.$t('backgroundInfo.options.item-1'),
          this.$root.$t('backgroundInfo.options.item-2'),
          this.$root.$t('backgroundInfo.options.item-3'),
        ],
        optionsAlias: ['tutoring', 'collegeCounseling', 'mentoring'],
      },
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
    hasCompletedBackgroundInfo() {
      return (
        this.user.occupation &&
        this.user.occupation.length > 0 &&
        this.user.country
      )
    },
    countries() {
      return COUNTRIES
    },
    states() {
      return STATES
    },
    isUnitedStatesSelected() {
      return this.country === this.$root.$t('backgroundInfo.unitedStateCountry')
    },
    linkedInUrlPattern() {
      return /https?:\/\/(www\.)?linkedin\.com.*\/in\/.{1,}$/
    },
    isValidLinkedInUrl() {
      if (!this.linkedInUrl) return true
      return this.linkedInUrlPattern.test(this.linkedInUrl)
    },
    isCollegeEducated() {
      return (
        this.occupation.includes(
          this.$root.$t('backgroundInfo.occupations.item-2')
        ) ||
        this.occupation.includes(
          this.$root.$t('backgroundInfo.occupations.item-3')
        )
      )
    },
    isWorkingFullTime() {
      return (
        this.occupation.includes(
          this.$root.$t('backgroundInfo.occupations.item-4')
        ) && !this.user.volunteerPartnerOrg
      )
    },
  },
  methods: {
    async submitForm(event) {
      event.preventDefault()

      this.showInputErrors = false
      this.formError = ''

      if (this.wasSubmitted) return
      if (this.invalidForm()) {
        this.showInputErrors = true
        this.formError = this.$root.$t('backgroundInfo.formError')
        return
      }
      this.wasSubmitted = true

      const { tutoring, collegeCounseling, mentoring } = this.experience
      const { columnTitle } = this.experienceRadioQuestion
      const experience = {
        tutoring: columnTitle[tutoring - 1],
        collegeCounseling: columnTitle[collegeCounseling - 1],
        mentoring: columnTitle[mentoring - 1],
      }

      const data = {
        occupation: this.occupation,
        experience,
        languages: this.languages,
        linkedInUrl: this.linkedInUrl,
        country: this.country,
        state: this.isUnitedStatesSelected ? this.state : '',
        city: this.city,
        college: this.college,
        company: this.company,
      }

      if (this.languages.length > 0) {
        const languages = Array.from(this.languages)
        if (this.addedLanguages) languages.push(this.addedLanguages)
        data.languages = languages
      }

      try {
        await NetworkService.addBackgroundInfo(data)
        this.wasSubmitted = false
        AnalyticsService.captureEvent(EVENTS.BACKGROUND_INFORMATION_COMPLETED, {
          event: EVENTS.BACKGROUND_INFORMATION_COMPLETED,
        })

        if (this.user.volunteerPartnerOrg)
          AnalyticsService.captureEvent(EVENTS.ACCOUNT_APPROVED, {
            event: EVENTS.ACCOUNT_APPROVED,
          })

        // mandatory fields: occupation, experience, country / state / city,
        // update is a subset of mandatory fields
        const update = {
          occupation: data.occupation,
          country: data.country,
        }

        this.$store.dispatch('user/addToUser', update)
      } catch (error) {
        this.formError = this.$root.$t('backgroundInfo.formError-2')
        this.wasSubmitted = false
      }
    },
    toggleAddLanguages() {
      this.showAddLanguages = !this.showAddLanguages
    },
    invalidForm() {
      const { tutoring, collegeCounseling, mentoring } = this.experience

      return (
        this.occupation.length === 0 ||
        !tutoring ||
        !collegeCounseling ||
        !mentoring ||
        !this.country ||
        !this.city ||
        (this.isUnitedStatesSelected && !this.state) ||
        !this.isValidLinkedInUrl ||
        (this.isCollegeEducated && !this.college) ||
        (this.isWorkingFullTime && !this.company)
      )
    },
  },
}
</script>

<style lang="scss" scoped>
input:invalid {
  border-bottom: $c-error-red solid 3px;
}

ol {
  padding-inline-start: 30px;
  @include breakpoint-above('medium') {
    padding-inline-start: 40px;
  }
}

.uc-form-checkbox {
  margin-bottom: 0.6em;

  & label {
    @include font-category('body');
  }
}

.uc-form-col {
  margin: 4em 0;
}

.uc-form-radio {
  margin-bottom: 0.6em;

  & label {
    margin-left: 15px;
  }
}

textarea {
  width: 100%;
  height: 80px;
}

.background-info {
  @include font-category('body');

  &__wrapper {
    max-width: 100%;
    background-color: #fff;
    border-radius: 8px;

    @include breakpoint-above('medium') {
      padding: 40px;
      max-width: 90%;
      margin: 15px 15px 55px 40px;
    }

    @include breakpoint-above('large') {
      max-width: 800px;
    }
  }

  &__header {
    margin: 0;
    text-align: left;
    font-weight: 500;
    padding: 40px 20px 20px 20px;
    @include font-category('display-small');

    @include breakpoint-above('medium') {
      padding: 20px;
    }
  }

  &__completed-message {
    padding: 1em;
  }

  &__form {
    border-radius: 8px;
    padding: 20px;
    text-align: left;
    max-width: 95%;

    @include breakpoint-above('large') {
      max-width: 80%;
    }
  }

  &__question-description {
    @include font-category('helper-text');
    margin-top: 10px;
    color: $c-secondary-grey;
  }

  &__question-required {
    color: $c-error-red;
  }
}

.location-label,
.occupations-label {
  display: block;
  margin-top: 1.4em;
  margin-bottom: 0.5em;
}

.linkedin-input,
.location-input,
.occupations-input {
  width: 90%;

  @include breakpoint-above('medium') {
    width: 80%;
  }
}

.submit-btn {
  width: 200px;
}

.error {
  color: $c-error-red;
}

.form-error {
  margin-bottom: 2em;
}

.question-row {
  overflow: hidden;
  width: 100%;
  display: inline-block;
  margin-bottom: 4em;

  @include breakpoint-above('medium') {
    width: 100%;
  }
}

.question-scroll-container {
  overflow-x: auto;
}

.position-wrapper {
  position: relative;
}

.mobile-pinned-questions-container {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  pointer-events: none;
}

.questions-container {
  padding: 1em;
  width: 100%;
  width: 100vw;

  @include breakpoint-above('medium') {
    width: 100%;
    padding: 4em;
  }
}

.questions-table {
  width: 100%;
  font-size: 13px;
  display: inline-block;

  @include breakpoint-above('large') {
    display: table;
    font-size: 15px;
  }
}

.radio-question-row:nth-child(even) {
  background: #f1f8fc;
}

.radio-question-row:nth-child(odd) {
  background: #e5f2fc;
}

.radio-question-row:nth-child(1) {
  background: white;
}

.radio-question-cell {
  width: 175px;
  padding: 1.4em 1.2em 1.6em 1em;

  &--shadow {
    box-shadow: 5px 0 5px -1px #e0e0e0;

    @media screen and (min-width: 620px) and (max-width: 770px) {
      box-shadow: none;
    }

    @include breakpoint-above('medium') {
      box-shadow: 5px 0 5px -1px #e0e0e0;
    }

    @include breakpoint-above('large') {
      box-shadow: none;
    }
  }
}

.radio-question-selection-title {
  display: table-cell;
  padding-left: 14px;
  padding-right: 14px;
  text-align: center;
  vertical-align: middle;
  padding-top: 8px;
  padding-bottom: 15px;

  &--hidden {
    visibility: hidden;
  }
}

.radio-question-selection-cell {
  text-align: center;
  vertical-align: middle;

  &--hidden {
    display: none;
  }
}

.mobile-remove {
  &--shadow {
    box-shadow: 5px 0 5px -1px #ffffff;

    @include breakpoint-above('medium') {
      box-shadow: none;
    }
  }
}

html.is-rtl {
  .uc-form-radio {
    & label {
      margin-right: 15px;
      margin-left: unset;
    }
  }

  .background-info {
    &__wrapper {
      @include breakpoint-above('medium') {
        margin: 15px 40px 55px 15px;
      }
    }

    &__header {
      text-align: right;
    }

    &__form {
      text-align: right;
    }
  }

  .mobile-pinned-questions-container {
    right: 0;
    left: unset;
  }

  .radio-question-cell {
    padding: 1.4em 1em 1.6em 1.2em;
  }
}
</style>
